import { Controller } from 'stimulus'
import Rails from '@rails/ujs'
import { useDebounce, useDispatch } from 'stimulus-use'

export default class extends Controller {
  static values = { issueId: Number }
  static targets = [
    'filtersForm', 'exportDetailsForm', 'token', 'pagination',
    'entitiesContainer', 'entitiesCounter', 'entitiesSelectedCounter',
    'selectBillCardsController', 'createExportButton', 'orderByForm',
    'loadingIndicator', 'loadingMoreResultsIndicator', 'noMoreResultsIndicator',
    'exportTitle', 'downloadBody'
  ]

  static debounces = ['reloadResults', 'loadMoreResults', 'loadResults']

  initialize() {
    this._firstLoad = true
    const options = { rootMargin: '2000px' }
    this.intersectionObserver = new IntersectionObserver(entries => this.processIntersectionEntries(entries), options)
  }

  connect() {
    useDebounce(this, { wait: 800 })
    useDispatch(this)
    this.intersectionObserver.observe(this.paginationTarget)
    this.resetFormsInputsValue()
    this.reloadResults()
    this.lastBillLoadedIndex = 0
    this.listenToClearAllEvent()
  }

  disconnect() {
    this.intersectionObserver.unobserve(this.paginationTarget)
  }

  listenToClearAllEvent() {
    this.filtersFormTarget.addEventListener('filters-container:filterCleared', this.reloadResults.bind(this), {})
  }

  resetFormsInputsValue() {
    this.filtersFormTarget.reset()
    this.exportDetailsFormTarget.reset()
  }

  setAsLoading() {
    this.isLoadingEntities = true
    this.dispatch('isLoading', { bubbles: true })

    //* -----
    // TODO - Move this outside this controller. This shouldn't be the responsability of this controller.
    //  This can be achieved with the use of the ClassTogglerController and the isLoading and isReady events
    if (this._firstLoad) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    } else {
      this.loadingMoreResultsIndicatorTarget.classList.remove('hidden')
    }
    // -----*

    this.noMoreResultsIndicatorTarget.classList.add('hidden')
    this.entitiesCounterTarget.innerHTML = 'Loading' // TODO - This is not the responsability of this controller. Find a way to move it to the select_bill_cards_controller
    this.entitiesSelectedCounterTarget.innerHTML = 'Loading' // TODO - This is not the responsability of this controller. Find a way to move it to the select_bill_cards_controller
    this.setDisabilityOnExportButtons(true)
  }

  setAsReady() {
    this.isLoadingEntities = false
    this.loadingIndicatorTarget.classList.add('hidden')
    this.loadingMoreResultsIndicatorTarget.classList.add('hidden')
    if (!this._firstLoad && !this.tokenTarget.value) {
      this.noMoreResultsIndicatorTarget.classList.remove('hidden')
    }
    if (this._firstLoad) {
      this.dispatch('firstLoadIsReady')
    }
    this.dispatch('isReady', { bubbles: true })
  }

  setElementsEditable(areEditable) {
    const filterFormElements = this.filtersFormTarget.elements
    const orderByFormElements = this.orderByFormTarget.elements

    for (let i = 0, len = filterFormElements.length; i < len; ++i) {
      if (areEditable) {
        filterFormElements[i].removeAttribute('readonly')
        filterFormElements[i].removeAttribute('disabled')
      } else {
        filterFormElements[i].setAttribute('readonly', 'true')
        filterFormElements[i].setAttribute('disabled', 'true')
      }
    }

    for (let i = 0, len = orderByFormElements.length; i < len; ++i) {
      if (areEditable) {
        orderByFormElements[i].removeAttribute('readonly')
        orderByFormElements[i].removeAttribute('disabled')
      } else {
        orderByFormElements[i].setAttribute('readonly', 'true')
        orderByFormElements[i].setAttribute('disabled', 'true')
      }
    }

    if (areEditable) {
      this.orderByFormTarget.removeAttribute('disabled', 'disabled')
    } else {
      this.orderByFormTarget.setAttribute('disabled', 'disabled')
    }
  }

  reloadResults() {
    if (this.isLoadingEntities) { return }
    this.lastBillLoadedIndex = 0
    this._firstLoad = true
    this.setAsLoading()
    this.tokenTarget.value = null
    const searchFiltersValue = this.getSearchData()
    this.setElementsEditable(false)
    const parametrizedSearchFiltersValue = $.param(searchFiltersValue)

    this.entitiesContainerTarget.innerHTML = ''

    const successCallback = (e) => {
      this.setAsReady()
      this.setElementsEditable(true)
      this.dispatch('resultsReloaded')
    }
    const errorCallback = (e) => {
      this.setAsReady()
      this.setElementsEditable(true)
    }

    this.dispatch('filtersTriggered')
    this.dispatch('reloadResults--resultsToLoadParams', { params: parametrizedSearchFiltersValue })
    this.loadResults(parametrizedSearchFiltersValue, successCallback, errorCallback)
  }

  loadMoreResults() {
    if (this.isLoadingEntities || !this.tokenTarget.value) { return }
    this._firstLoad = false
    this.updateLastBillLoadedIndex()
    this.noMoreResultsIndicatorTarget.classList.add('hidden')
    this.setAsLoading()
    const searchFiltersValue = this.getSearchData()
    this.setElementsEditable(false)
    const parametrizedSearchFiltersValue = $.param(searchFiltersValue)

    const successCallback = (e) => {
      this.selectAllRecentBillsLoadedIfUnseenBillsSelected()
      this.setAsReady()
      this.setElementsEditable(true)
    }
    const errorCallback = (e) => {
      this.setAsReady()
      this.setElementsEditable(true)
    }

    this.loadResults(parametrizedSearchFiltersValue, successCallback, errorCallback)
  }

  loadResults(resultsToLoadParams, successCallback = null, errorCallback = null) {
    Rails.ajax({
      type: 'POST',
      url: '/omni_searches',
      dataType: 'script',
      data: resultsToLoadParams,
      success: () => {
        if (successCallback) { successCallback() }
      },
      error: (response, status, xhr) => {
        if (errorCallback) { errorCallback() }
        if (status !== 'Unauthorized') {
          alert('Something went wrong. Please try again later.')
        }
      }
    })
  }

  processIntersectionEntries(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        console.log('intersection observed')
        this.loadMoreResults()
      }
    })
  }

  createOrUpdateExport() {
    if (this.areAllCreateExportButtonsEnabled()) {
      const exportData = this.getDataToCreateExport()
      const parametrizedExportData = $.param(exportData)
      const exportId = this.data.get('exportId')

      if (exportId) {
        this.updateExport(parametrizedExportData, exportId)
      } else {
        this.createExport(parametrizedExportData)
      }
    } else {
      console.log('Missing to select at least one export')
    }
  }

  createExport(data) {
    Rails.ajax({
      type: 'POST',
      url: '/exports',
      dataType: 'json',
      data: data,
      success: (response) => {
        this.dispatch(
          'exportCreated', { exportData: $.deparam(data) }
        )
      },
      error: (data) => {
        // TODO
        alert('Something went wrong')
      }
    })
  }

  updateExport(data, exportId) {
    Rails.ajax({
      type: 'PUT',
      url: `/exports/${exportId}`,
      dataType: 'json',
      data: data,
      success: () => {
        this.dispatch(
          'exportUpdated', {
            exportData: $.deparam(data),
            exportId: exportId
          }
        )
      },
      error: (data) => {
        // TODO
        alert('Something went wrong')
      }
    })
  }

  getSearchData() {
    const searchData = this.getFilterFormInputsValue()
    Object.assign(searchData.search_attributes,
      this.getOrderByFormInputsValue().search_attributes)
    return searchData
  }

  getDataToCreateExport() {
    const createData = this.getFilterFormInputsValue()

    Object.assign(createData.search_attributes,
      this.getIncludedExcludedEntitiesId(),
      this.getOrderByFormInputsValue().search_attributes)

    Object.assign(createData, this.getExportDetailsFormInputsValue())

    return createData
  }

  getExportDetailsFormInputsValue() {
    return $.deparam($(this.exportDetailsFormTarget).serialize())
  }

  getOrderByFormInputsValue() {
    return $.deparam($(this.orderByFormTarget).serialize())
  }

  getFilterFormInputsValue() {
    return $.deparam($(this.filtersFormTarget).serialize())
  }

  getIncludedExcludedEntitiesId() {
    const selectBillCardsController = this.getSelectBillCardsController()
    const checkboxesValues = []

    if (selectBillCardsController.areUnseenBillsSelected) {
      selectBillCardsController.checkboxesUnchecked().forEach((elem) => {
        checkboxesValues.push(elem.value)
      })

      return {
        excluded_ids: checkboxesValues,
        included_ids: []
      }
    } else {
      selectBillCardsController.checkboxesChecked().forEach((elem) => {
        checkboxesValues.push(elem.value)
      })

      return {
        excluded_ids: [],
        included_ids: checkboxesValues
      }
    }
  }

  areAllCreateExportButtonsEnabled() {
    const isAtLeastOneButtonDisabled = this.createExportButtonTargets.some((button) => { return button.hasAttribute('disabled') })
    return !isAtLeastOneButtonDisabled
  }

  refreshCreateExportButton() {
    const selectBillCardsController = this.getSelectBillCardsController()
    const billsSelectedAmount = selectBillCardsController.checkboxesCheckedCount()
    if (!Number.isFinite(billsSelectedAmount)) { this.setDisabilityOnExportButtons(true) } else { this.setDisabilityOnExportButtons(billsSelectedAmount <= 0) }
  }

  setDisabilityOnExportButtons(isDisabled) {
    const buttonDisabledClass = 'c-button--state-disabled'
    if (isDisabled) {
      this.createExportButtonTargets.forEach((button) => {
        button.setAttribute('disabled', 'disabled')
        button.classList.add(buttonDisabledClass)
      })
    } else {
      this.createExportButtonTargets.forEach((button) => {
        button.removeAttribute('disabled')
        button.classList.remove(buttonDisabledClass)
      })
    }
  }

  triggerSelectAllBillCards() {
    const selectBillCardsController = this.getSelectBillCardsController()
    selectBillCardsController.selectAllBills()
  }

  selectAllRecentBillsLoadedIfUnseenBillsSelected() {
    const selectBillCardsController = this.getSelectBillCardsController()

    if (selectBillCardsController.areUnseenBillsSelected) {
      selectBillCardsController.selectAllBillsFromIndexToEnd(this.lastBillLoadedIndex)
    }
  }

  updateLastBillLoadedIndex() {
    const selectBillCardsController = this.getSelectBillCardsController()
    this.lastBillLoadedIndex = selectBillCardsController.checkboxes().length - 1
  }

  getSelectBillCardsController() {
    return this.selectBillCardsControllerTarget['select-bill-cards']
  }

  notifyLabelsLogicalOperatorChanged() {
    const searchData = this.getSearchData()
    if (searchData.search_attributes.labels_ids) {
      this.reloadResults()
    } else {
      // 'Query should only be triggered if labels have been selected'
      console.log('Labels not selected')
    }
  }
}
